import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { askSubAgentPhase1, askSubAgentPhase2ExecuteAll, getConversationHistory, executeQueryWithParameters, deleteQueryExecution, deleteConversation, checkAgentSufficiency, createRequiredAgents } from '../services/agentApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import {
    MessageSquare,
    Send,
    Bot,
    User,
    Loader,
    Clock,
    TrendingUp,
    AlertCircle,
    Plus,
    Menu,
    X,
    Database,
    ChevronDown,
    ArrowLeft,
    Play,
    Trash2
} from 'lucide-react';
import { PlainQueryDisplay } from '../components/ui/PlainQueryDisplay';

/**
 * AgentChat - ChatGPT-style layout with original styling
 * - Left sidebar: Conversation history
 * - Main area: Current chat with Q&A display
 */
export const AgentChat = () => {
    const { t } = useTranslation(['page-agent-chat', 'common']);
    const navigate = useNavigate();
    const location = useLocation();
    const { databases } = useDatabase();
    const { showToast } = useToast();

    const databaseFromState = location.state?.database;
    const { databaseId } = useParams();

    const [loading, setLoading] = useState(false);
    const [question, setQuestion] = useState('');
    const [chats, setChats] = useState([]);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [currentDatabase, setCurrentDatabase] = useState(null);
    const [expandedResults, setExpandedResults] = useState({});
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [phase1Data, setPhase1Data] = useState(null);  // Store Phase 1 response
    const [userParameters, setUserParameters] = useState({});  // Store user-entered parameters
    const [isPhase2Executing, setIsPhase2Executing] = useState(false);  // Phase 2 execution state
    const messagesEndRef = useRef(null);

    // Parameter collection state
    const [showParameterDialog, setShowParameterDialog] = useState(false);
    const [parameterRequirements, setParameterRequirements] = useState([]);
    const [parameterValues, setParameterValues] = useState({});
    const [pendingQueryData, setPendingQueryData] = useState(null);
    const [pendingChatId, setPendingChatId] = useState(null);

    // Agent sufficiency check state
    const [showAgentSufficiencyModal, setShowAgentSufficiencyModal] = useState(false);
    const [agentSufficiencyData, setAgentSufficiencyData] = useState(null);
    const [pendingQuestion, setPendingQuestion] = useState(null);
    const [pendingChatTempId, setPendingChatTempId] = useState(null);
    const [isCreatingAgents, setIsCreatingAgents] = useState(false);

    // Confirmation modal state for conversations
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        chatId: null,
        title: t('page-agent-chat:confirm.deleteConversationTitle'),
        message: t('page-agent-chat:confirm.deleteConversationMessage'),
    });

    // Confirmation modal state for execution results
    const [executionConfirmModal, setExecutionConfirmModal] = useState({
        isOpen: false,
        chatId: null,
        messageIdx: null,
        resultIdx: null,
        executionId: null,
        title: t('page-agent-chat:confirm.deleteExecutionTitle'),
        message: t('page-agent-chat:confirm.deleteExecutionMessage'),
    });

    // Load current database
    useEffect(() => {
        const db = databaseFromState || databases.find(db => db.id === parseInt(databaseId));
        if (db) {
            setCurrentDatabase(db);
        }
    }, [databaseFromState, databases, databaseId, location.pathname]);

    // Load chats from database API
    const loadChatsFromDatabase = useCallback(async () => {
        if (!currentDatabase) return;
        try {
            const response = await getConversationHistory(currentDatabase.id);
            if (response.data?.status === 'success') {
                const plans = response.data.data?.plans || [];

                // Convert agent plans to chat format
                const chatItems = plans.map((plan) => {
                    // Calculate step statistics
                    const stepsCompleted = plan.steps_completed || 0;
                    const stepsFailed = plan.steps_failed || 0;
                    const totalSteps = plan.steps_count || plan.steps?.length || 0;
                    
                    return {
                        id: plan.id,
                        title: plan.user_question,
                        messages: [{
                            id: 'msg_' + plan.id,
                            role: 'user',
                            content: plan.user_question,
                            timestamp: plan.created_at
                        }, {
                            id: 'msg_' + plan.id + '_ai',
                            role: 'assistant',
                            content: plan.final_answer || t('page-agent-chat:chatContent.noAnswerAvailable'),
                            reasoning: plan.reasoning,
                            results: plan.results || [],
                            steps: plan.steps || [],
                            stepExecutionSummary: plan.step_execution_summary || [],
                            stepsCompleted,
                            stepsFailed,
                            totalSteps,
                            timestamp: plan.completed_at || plan.created_at,
                            status: plan.status,
                            error: plan.error
                        }],
                        timestamp: plan.created_at,
                        status: plan.status
                    };
                });

                setChats(chatItems);
            }
        } catch (error) {
            console.error('Failed to load chat history:', error);
        }
    }, [currentDatabase, t]);

    useEffect(() => {
        if (currentDatabase) {
            loadChatsFromDatabase();
        }
    }, [currentDatabase, loadChatsFromDatabase]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chats, currentChatId]);

    const handleSendMessage = async () => {
        if (!question.trim() || !currentDatabase) {
            showToast(t('page-agent-chat:toast.enterQuestion'), 'error');
            return;
        }

        setLoading(true);
        const userQuestion = question;
        setQuestion('');
        const timestamp = new Date().toISOString();

        // Create a new chat with user message immediately
        const tempChatId = 'chat_' + Date.now();
        const newChat = {
            id: tempChatId,
            title: userQuestion,
            messages: [{
                id: 'msg_user_' + Date.now(),
                role: 'user',
                content: userQuestion,
                timestamp: timestamp
            }, {
                id: 'msg_ai_' + Date.now(),
                role: 'assistant',
                content: '',
                isLoading: true,
                timestamp: timestamp
            }],
            timestamp: timestamp
        };

        // Add the chat with user message and loading AI message immediately
        setChats(prev => [newChat, ...prev]);
        setCurrentChatId(newChat.id);

        try {
            // STEP 1: Check if existing agents are sufficient
            const sufficiencyResponse = await checkAgentSufficiency(currentDatabase.id, userQuestion);

            if (sufficiencyResponse.data?.status === 'success') {
                const data = sufficiencyResponse.data.data;

                // If question is not database-specific, show general response
                if (!data.is_database_specific) {
                    setChats(prev => prev.map(chat => {
                        if (chat.id === tempChatId) {
                            return {
                                ...chat,
                                messages: [{
                                    id: 'msg_user_' + Date.now(),
                                    role: 'user',
                                    content: userQuestion,
                                    timestamp: timestamp
                                }, {
                                    id: 'msg_ai_' + Date.now(),
                                    role: 'assistant',
                                    content: data.reasoning || t('page-agent-chat:chatContent.notDatabaseRelated'),
                                    timestamp: timestamp,
                                    isGeneralResponse: true,
                                    isLoading: false
                                }]
                            };
                        }
                        return chat;
                    }));
                    setLoading(false);
                    return;
                }

                // If agents are sufficient, proceed with Phase 1
                if (data.is_sufficient === true) {
                    // PHASE 1: Generate queries and get parameters
                    const response = await askSubAgentPhase1(currentDatabase.id, userQuestion);

                    if (response.data?.status === 'success') {
                        const phase1Data = response.data.data;

                        // Store Phase 1 data for Phase 2 execution
                        setPhase1Data({
                            nlpQaId: phase1Data.nlp_qa_id,  // Changed from planId to nlpQaId
                            queriesWithParameters: phase1Data.queries_with_parameters || [],
                            userQuestion: userQuestion,
                            reasoning: phase1Data.reasoning,
                            steps: phase1Data.steps || []
                        });

                        // Update the chat with Phase 1 response (showing parameter input form)
                        setChats(prev => prev.map(chat => {
                            if (chat.id === tempChatId) {
                                return {
                                    ...chat,
                                    messages: [{
                                        id: 'msg_user_' + Date.now(),
                                        role: 'user',
                                        content: userQuestion,
                                        timestamp: timestamp
                                    }, {
                                        id: 'msg_ai_' + Date.now(),
                                        role: 'assistant',
                                        content: t('page-agent-chat:chatContent.queriesGenerated'),
                                        reasoning: phase1Data.reasoning,
                                        steps: phase1Data.steps || [],
                                        queriesWithParameters: phase1Data.queries_with_parameters || [],
                                        timestamp: timestamp,
                                        isLoading: false,
                                        waitingForParameters: true
                                    }]
                                };
                            }
                            return chat;
                        }));
                    }
                } else if (data.is_sufficient === false) {
                    // Agents are NOT sufficient - show modal to user
                    setAgentSufficiencyData(data);
                    setPendingQuestion(userQuestion);
                    setPendingChatTempId(tempChatId);
                    setShowAgentSufficiencyModal(true);
                    
                    // Update chat to show waiting state
                    setChats(prev => prev.map(chat => {
                        if (chat.id === tempChatId) {
                            return {
                                ...chat,
                                messages: [{
                                    id: 'msg_user_' + Date.now(),
                                    role: 'user',
                                    content: userQuestion,
                                    timestamp: timestamp
                                }, {
                                    id: 'msg_ai_' + Date.now(),
                                    role: 'assistant',
                                    content: t('page-agent-chat:chatContent.checkingAgentAvailability'),
                                    timestamp: timestamp,
                                    isLoading: true
                                }]
                            };
                        }
                        return chat;
                    }));
                }
            }
        } catch (error) {
            console.error('Failed to check agent sufficiency:', error);
            showToast(error.response?.data?.detail || t('page-agent-chat:toast.failedToProcess'), 'error');

            // Update with error message
            setChats(prev => prev.map(chat => {
                if (chat.id === tempChatId) {
                    return {
                        ...chat,
                        messages: [{
                            id: 'msg_user_' + Date.now(),
                            role: 'user',
                            content: userQuestion,
                            timestamp: timestamp
                        }, {
                            id: 'msg_ai_' + Date.now(),
                            role: 'assistant',
                            content: t('page-agent-chat:chatContent.errorProcessing'),
                            timestamp: timestamp,
                            isError: true,
                            isLoading: false
                        }]
                    };
                }
                return chat;
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleParameterChange = (queryId, paramName, value) => {
        setUserParameters(prev => ({
            ...prev,
            [queryId]: {
                ...prev[queryId],
                [paramName]: value
            }
        }));
    };

    const handleExecutePhase2 = async () => {
        if (!phase1Data) {
            showToast(t('page-agent-chat:toast.noPhase1Data'), 'error');
            return;
        }

        setIsPhase2Executing(true);
        const timestamp = new Date().toISOString();

        try {
            // PHASE 2: Execute all queries with user parameters
            const response = await askSubAgentPhase2ExecuteAll(
                currentDatabase.id,
                phase1Data.nlpQaId,  // Changed from planId to nlpQaId
                userParameters
            );

            if (response.data?.status === 'success') {
                const data = response.data.data;
                
                // Update the chat with Phase 2 results
                setChats(prev => prev.map(chat => {
                    if (chat.id === currentChatId) {
                        return {
                            ...chat,
                            messages: [{
                                id: 'msg_user_' + Date.now(),
                                role: 'user',
                                content: phase1Data.userQuestion,
                                timestamp: timestamp
                            }, {
                                id: 'msg_ai_' + Date.now(),
                                role: 'assistant',
                                content: data.final_answer || t('page-agent-chat:chatContent.analysisComplete'),
                                reasoning: phase1Data.reasoning,
                                steps: phase1Data.steps || [],
                                results: data.execution_results || [],
                                stepExecutionSummary: data.execution_results?.map(r => ({
                                    agent_name: r.agent_name,
                                    task: r.task,
                                    status: r.execution_status || 'completed',
                                    data: r.data,
                                    sql: r.execution_results?.[0]?.sql,
                                    error: r.error
                                })) || [],
                                timestamp: timestamp,
                                isLoading: false,
                                waitingForParameters: false
                            }]
                        };
                    }
                    return chat;
                }));

                // Clear Phase 1 data
                setPhase1Data(null);
                setUserParameters({});
                
                // Reload chat history
                await new Promise(resolve => setTimeout(resolve, 800));
                const historyResponse = await getConversationHistory(currentDatabase.id);
                if (historyResponse.data?.status === 'success') {
                    const plans = historyResponse.data.data?.plans || [];
                    const chatItems = plans.map((plan) => {
                        const stepsCompleted = plan.steps_completed || 0;
                        const stepsFailed = plan.steps_failed || 0;
                        const totalSteps = plan.steps_count || plan.steps?.length || 0;

                        return {
                            id: plan.id,
                            title: plan.user_question,
                            messages: [{
                                id: 'msg_' + plan.id,
                                role: 'user',
                                content: plan.user_question,
                                timestamp: plan.created_at
                            }, {
                                id: 'msg_' + plan.id + '_ai',
                                role: 'assistant',
                                content: plan.final_answer || t('page-agent-chat:chatContent.noAnswerAvailable'),
                                reasoning: plan.reasoning,
                                results: plan.results || [],
                                steps: plan.steps || [],
                                stepExecutionSummary: plan.step_execution_summary || [],
                                stepsCompleted,
                                stepsFailed,
                                totalSteps,
                                timestamp: plan.completed_at || plan.created_at,
                                status: plan.status,
                                error: plan.error
                            }],
                            timestamp: plan.created_at,
                            status: plan.status
                        };
                    });

                    setChats(chatItems);
                    const newChatFromDb = chatItems.find(chat => chat.title === phase1Data.userQuestion);
                    if (newChatFromDb) {
                        setCurrentChatId(newChatFromDb.id);
                    }
                }
            }
        } catch (error) {
            console.error('Phase 2 execution failed:', error);
            showToast(error.response?.data?.detail || t('page-agent-chat:toast.failedToExecuteQueries'), 'error');
        } finally {
            setIsPhase2Executing(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const selectChat = (chatId) => {
        setCurrentChatId(chatId);
    };

    const createNewChat = () => {
        setCurrentChatId(null);
        setExpandedResults({});
    };

    const handleDeleteConversation = useCallback(async (chatId) => {
        setConfirmModal({
            isOpen: true,
            chatId: chatId,
            title: t('page-agent-chat:confirm.deleteConversationTitle'),
            message: t('page-agent-chat:confirm.deleteConversationMessage'),
        });
    }, [t]);

    const confirmDeleteConversation = useCallback(async () => {
        if (!confirmModal.chatId) return;

        try {
            const response = await deleteConversation(confirmModal.chatId);
            if (response.data?.status === 'success') {
                showToast(t('page-agent-chat:toast.conversationDeleted'), 'success');
                // Remove from chats list
                setChats(prev => prev.filter(chat => chat.id !== confirmModal.chatId));
                // If current chat is deleted, clear it
                if (currentChatId === confirmModal.chatId) {
                    setCurrentChatId(null);
                }
                // Reload chats to ensure sync
                await loadChatsFromDatabase();
            } else {
                showToast(t('page-agent-chat:toast.failedToDeleteConversation'), 'error');
            }
        } catch (error) {
            console.error('Failed to delete conversation:', error);
            showToast(error?.response?.data?.message || t('page-agent-chat:toast.failedToDeleteConversation'), 'error');
        }

        setConfirmModal(prev => ({ ...prev, isOpen: false, chatId: null }));
    }, [confirmModal.chatId, currentChatId, loadChatsFromDatabase, showToast, t]);

    const toggleResultExpand = useCallback((resultIdx) => {
        setExpandedResults(prev => ({
            ...prev,
            [resultIdx]: !prev[resultIdx]
        }));
    }, []);

    // Parameter collection handlers
    const handleParameterSubmit = async () => {
        if (!pendingQueryData || !pendingChatId) return;

        // Validate all required parameters are filled
        const missingParams = parameterRequirements.filter(param => {
            if (param.required && (!parameterValues[param.name] || parameterValues[param.name].trim() === '')) {
                return true;
            }
            return false;
        });

        if (missingParams.length > 0) {
            showToast(t('page-agent-chat:toast.missingParameters', { params: missingParams.map(p => p.name).join(', ') }), 'error');
            return;
        }

        setLoading(true);
        setShowParameterDialog(false);

        try {
            // Execute query with user-provided parameters
            const response = await executeQueryWithParameters(
                pendingQueryData.generated_query_id,
                currentDatabase.id,
                parameterValues
            );

            if (response.data?.status === 'success') {
                const executionData = response.data.data;

                // Update chat with execution results
                setChats(prev => prev.map(chat => {
                    if (chat.id === pendingChatId) {
                        return {
                            ...chat,
                            messages: chat.messages.map(msg => {
                                if (msg.id === pendingQueryData.pendingMessageId) {
                                    return {
                                        ...msg,
                                        content: pendingQueryData.refinedContent || msg.content,
                                        results: [{
                                            data: executionData.rows || executionData.data || [],
                                            execution: {
                                                id: executionData.query_result?.id,  // Store execution ID for deletion
                                                sql: pendingQueryData.query,
                                                agent_name: pendingQueryData.name,
                                                task: pendingQueryData.description,
                                                data: executionData.rows || executionData.data || []
                                            },
                                            status: 'completed'
                                        }],
                                        isLoading: false
                                    };
                                }
                                return msg;
                            })
                        };
                    }
                    return chat;
                }));

                showToast(t('page-agent-chat:toast.queryExecuted'), 'success');
            }
        } catch (error) {
            console.error('Failed to execute query with parameters:', error);
            showToast(error.response?.data?.detail || t('page-agent-chat:toast.failedToExecuteParams'), 'error');

            // Update chat with error
            setChats(prev => prev.map(chat => {
                if (chat.id === pendingChatId) {
                    return {
                        ...chat,
                        messages: chat.messages.map(msg => {
                            if (msg.id === pendingQueryData.pendingMessageId) {
                                return {
                                    ...msg,
                                    content: t('page-agent-chat:chatContent.errorExecutingQuery', { detail: error.response?.data?.detail || error.message }),
                                    isError: true,
                                    isLoading: false
                                };
                            }
                            return msg;
                        })
                    };
                }
                return chat;
            }));
        } finally {
            setLoading(false);
            setPendingQueryData(null);
            setPendingChatId(null);
            setParameterValues({});
        }
    };

    const handleParameterCancel = () => {
        setShowParameterDialog(false);
        setParameterRequirements([]);
        setParameterValues({});
        setPendingQueryData(null);
        setPendingChatId(null);

        // Update chat to show cancelled state
        if (pendingChatId && pendingQueryData?.pendingMessageId) {
            setChats(prev => prev.map(chat => {
                if (chat.id === pendingChatId) {
                    return {
                        ...chat,
                        messages: chat.messages.map(msg => {
                            if (msg.id === pendingQueryData.pendingMessageId) {
                                return {
                                    ...msg,
                                    content: msg.content + t('page-agent-chat:chatContent.executionCancelled'),
                                    isLoading: false
                                };
                            }
                            return msg;
                        })
                    };
                }
                return chat;
            }));
        }
    };

    // Agent sufficiency modal handlers
    const handleCreateAgents = async () => {
        if (!pendingQuestion || !currentDatabase) return;

        setIsCreatingAgents(true);
        
        try {
            // Create the required agents
            const response = await createRequiredAgents(currentDatabase.id, pendingQuestion);

            if (response.data?.status === 'success') {
                showToast(t('page-agent-chat:toast.agentsCreated', { count: response.data.data.sub_agents_count }), 'success');
                
                // Close modal
                setShowAgentSufficiencyModal(false);
                setAgentSufficiencyData(null);
                setPendingQuestion(null);
                
                // Now proceed with Phase 1 using the newly created agents
                await proceedWithPhase1AfterAgentCreation();
            }
        } catch (error) {
            console.error('Failed to create agents:', error);
            showToast(error.response?.data?.detail || t('page-agent-chat:toast.failedToCreateAgents'), 'error');
        } finally {
            setIsCreatingAgents(false);
        }
    };

    const proceedWithPhase1AfterAgentCreation = async () => {
        if (!pendingQuestion || !currentDatabase || !pendingChatTempId) return;

        const timestamp = new Date().toISOString();

        try {
            // PHASE 1: Generate queries and get parameters
            const response = await askSubAgentPhase1(currentDatabase.id, pendingQuestion);

            if (response.data?.status === 'success') {
                const phase1Data = response.data.data;

                // Store Phase 1 data for Phase 2 execution
                setPhase1Data({
                    planId: phase1Data.plan_id,
                    queriesWithParameters: phase1Data.queries_with_parameters || [],
                    userQuestion: pendingQuestion,
                    reasoning: phase1Data.reasoning,
                    steps: phase1Data.steps || []
                });

                // Update the chat with Phase 1 response
                setChats(prev => prev.map(chat => {
                    if (chat.id === pendingChatTempId) {
                        return {
                            ...chat,
                            messages: [{
                                id: 'msg_user_' + Date.now(),
                                role: 'user',
                                content: pendingQuestion,
                                timestamp: timestamp
                            }, {
                                id: 'msg_ai_' + Date.now(),
                                role: 'assistant',
                                content: t('page-agent-chat:chatContent.queriesGenerated'),
                                reasoning: phase1Data.reasoning,
                                steps: phase1Data.steps || [],
                                queriesWithParameters: phase1Data.queries_with_parameters || [],
                                timestamp: timestamp,
                                isLoading: false,
                                waitingForParameters: true
                            }]
                        };
                    }
                    return chat;
                }));
            }
        } catch (error) {
            console.error('Failed to get agent response after agent creation:', error);
            showToast(error.response?.data?.detail || t('page-agent-chat:toast.failedToGetAgentResponse'), 'error');
        } finally {
            setPendingQuestion(null);
            setPendingChatTempId(null);
        }
    };

    const handleProceedWithoutCreatingAgents = () => {
        // User chose not to create new agents
        // Show message that we'll proceed with existing agents or can't answer
        const timestamp = new Date().toISOString();

        setChats(prev => prev.map(chat => {
            if (chat.id === pendingChatTempId) {
                return {
                    ...chat,
                    messages: [{
                        id: 'msg_user_' + Date.now(),
                        role: 'user',
                        content: pendingQuestion,
                        timestamp: timestamp
                    }, {
                        id: 'msg_ai_' + Date.now(),
                        role: 'assistant',
                        content: agentSufficiencyData?.reasoning || t('page-agent-chat:chatContent.noAgentsFallback'),
                        timestamp: timestamp,
                        isLoading: false
                    }]
                };
            }
            return chat;
        }));

        // Close modal and clear state
        setShowAgentSufficiencyModal(false);
        setAgentSufficiencyData(null);
        setPendingQuestion(null);
        setPendingChatTempId(null);
    };

    const handleCancelAgentCreation = () => {
        // User cancelled - just close the modal
        setShowAgentSufficiencyModal(false);
        setAgentSufficiencyData(null);
        setPendingQuestion(null);
        setPendingChatTempId(null);
        
        // Optionally remove the chat message or show cancelled state
        if (pendingChatTempId) {
            setChats(prev => prev.filter(chat => chat.id !== pendingChatTempId));
        }
    };

    const handleLocalParameterChange = (paramName, value) => {
        setParameterValues(prev => ({
            ...prev,
            [paramName]: value
        }));
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        if (diffHours < 1) {
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            return t('page-agent-chat:time.minutesAgo', { count: diffMinutes });
        } else if (diffHours < 24) {
            return t('page-agent-chat:time.hoursAgo', { count: diffHours });
        } else {
            return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        }
    };

    const renderStepExecutionSummary = useCallback((stepSummary) => {
        if (!stepSummary || stepSummary.length === 0) return null;

        return (
            <div className="mb-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900 text-sm">{t('page-agent-chat:executionPlan.heading')}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{t('page-agent-chat:executionPlan.subtitle')}</p>
                </div>
                <div className="p-4 space-y-3">
                    {stepSummary.map((step, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                                step.status === 'completed' ? 'bg-green-100' :
                                step.status === 'failed' ? 'bg-red-100' : 'bg-gray-100'
                            }`}>
                                {step.status === 'completed' ? (
                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : step.status === 'failed' ? (
                                    <X className="w-4 h-4 text-red-600" />
                                ) : (
                                    <Clock className="w-4 h-4 text-gray-600" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{step.agent_name || `Agent ${idx + 1}`}</p>
                                <p className="text-xs text-gray-500 truncate">{step.task}</p>
                                {step.error && (
                                    <p className="text-xs text-red-600 mt-1">
                                        {typeof step.error === 'string' ? step.error : JSON.stringify(step.error)}
                                    </p>
                                )}
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                step.status === 'completed' ? 'bg-green-100 text-green-700' :
                                step.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                                {step.status || 'pending'}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [t]);

    const handleDeleteExecution = useCallback((chatId, messageIdx, resultIdx, executionId) => {
        setExecutionConfirmModal({
            isOpen: true,
            chatId: chatId,
            messageIdx: messageIdx,
            resultIdx: resultIdx,
            executionId: executionId,
            title: t('page-agent-chat:confirm.deleteExecutionTitle'),
            message: t('page-agent-chat:confirm.deleteExecutionMessage'),
        });
    }, [setExecutionConfirmModal, t]);

    const confirmDeleteExecution = useCallback(async () => {
        if (!executionConfirmModal.executionId) return;

        try {
            const response = await deleteQueryExecution(executionConfirmModal.executionId);
            if (response.data?.status === 'success') {
                showToast('Execution result deleted successfully', 'success');
                // Remove the result from the message
                setChats(prev => prev.map(chat => {
                    if (chat.id === executionConfirmModal.chatId) {
                        return {
                            ...chat,
                            messages: chat.messages.map((msg, mIdx) => {
                                if (mIdx === executionConfirmModal.messageIdx) {
                                    const newResults = msg.results.filter((_, rIdx) => rIdx !== executionConfirmModal.resultIdx);
                                    return {
                                        ...msg,
                                        results: newResults.length > 0 ? newResults : msg,
                                    };
                                }
                                return msg;
                            })
                        };
                    }
                    return chat;
                }));
            } else {
                showToast('Failed to delete execution', 'error');
            }
        } catch (error) {
            console.error('Failed to delete execution:', error);
            showToast(error?.response?.data?.message || 'Failed to delete execution', 'error');
        }
        
        setExecutionConfirmModal(prev => ({ ...prev, isOpen: false }));
    }, [executionConfirmModal, showToast, setChats]);

    const renderResultCard = useCallback((result, resultIdx, chatId, messageIdx) => {
        if (!result) return null;

        const isExpanded = expandedResults[resultIdx];

        // Support multiple result formats:
        // 1. New step-based format: result.execution contains the actual data
        // 2. Old format: result.data directly contains the array
        let resultData = result.data || [];
        let displayName = result.agent_name || result.insight_name || 'Query Result';
        let displayTask = result.task || result.insight_description || result.query || '';
        let status = result.status || result.execution_status || 'pending';
        const hasData = resultData && Array.isArray(resultData) && resultData.length > 0;
        const rowCount = result.row_count || (hasData ? resultData.length : 0);

        // If this is a step-based result with execution data
        if (result.execution) {
            resultData = result.execution.data || [];
            displayName = result.execution.agent_name || result.step?.agent_name || displayName;
            displayTask = result.execution.task || result.step?.task || displayTask;
            status = result.status || 'completed';
        }

        // Handle case where data might be in columns/rows format
        if (resultData && typeof resultData === 'object' && !Array.isArray(resultData)) {
            resultData = resultData.rows || resultData.data || [];
        }

        // Ensure resultData is an array (may be empty)
        if (!Array.isArray(resultData)) {
            resultData = [];
        }

        return (
            <div className="bg-white rounded-lg border border-gray-200 mb-3 overflow-hidden">
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleResultExpand(resultIdx);
                    }}
                    className="px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors flex items-center justify-between"
                >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <TrendingUp className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90 text-primary-600' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm truncate">{displayName}</h4>
                            {displayTask && (
                                <p className="text-xs text-gray-500 truncate mt-0.5">{displayTask}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                                {status && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                        status === 'completed' ? 'bg-green-100 text-green-700' :
                                        status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                                    }`}>
                                        {status}
                                    </span>
                                )}
                                {!hasData && (
                                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-700">
                                        No Data
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                            hasData ? 'text-gray-500 bg-gray-200' : 'text-yellow-600 bg-yellow-100'
                        }`}>
                            {rowCount} rows
                        </span>
                        {chatId !== undefined && messageIdx !== undefined && result.execution?.id && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteExecution(chatId, messageIdx, resultIdx, result.execution.id);
                                }}
                                className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Delete this result"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400 rotate-180" />
                        )}
                    </div>
                </div>

                {isExpanded && (
                    <div className="p-4 space-y-4">
                        {result.execution?.sql || result.sql ? (
                            <PlainQueryDisplay sql={result.execution?.sql || result.sql} />
                        ) : null}

                        {hasData ? (
                            <div>
                                <p className="text-xs font-medium text-gray-500 mb-2">Results</p>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                {Object.keys(resultData[0]).map((key, idx) => (
                                                    <th key={idx} className="text-left py-2 px-3 font-medium text-gray-700">
                                                        {key.replace(/_/g, ' ')}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resultData.map((row, rowIdx) => (
                                                <tr key={rowIdx} className="border-b border-gray-100 hover:bg-gray-50">
                                                    {Object.values(row).map((value, colIdx) => (
                                                        <td key={colIdx} className="py-2 px-3 text-gray-600">
                                                            {typeof value === 'number' ? value.toLocaleString() : value}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {resultData.length > 5 && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            Showing {resultData.length} rows
                                        </p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="flex flex-col items-center justify-center">
                                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-3">
                                        <AlertCircle className="w-6 h-6 text-yellow-600" />
                                    </div>
                                    <p className="text-sm font-medium text-gray-900 mb-1">No Data Returned</p>
                                    <p className="text-xs text-gray-500 max-w-md">
                                        This agent executed successfully but no matching data was found. 
                                        This could be due to filter criteria, date ranges, or the database not containing relevant records.
                                    </p>
                                </div>
                            </div>
                        )}

                        {(result.error || result.execution?.error) && (
                            <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg mt-3">
                                <AlertCircle className="w-4 h-4" />
                                <span className="text-sm">
                                    {typeof (result.error || result.execution?.error) === 'string' 
                                        ? (result.error || result.execution?.error)
                                        : JSON.stringify(result.error || result.execution?.error)}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }, [expandedResults, toggleResultExpand, handleDeleteExecution]);

    const currentChat = chats.find(chat => chat.id === currentChatId);

    if (!currentDatabase) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader className="w-12 h-12 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-gray-50 overflow-hidden">
            {/* Sidebar - History */}
            <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300 ease-in-out absolute lg:relative z-20 h-full w-80 flex-shrink-0`}>
                <div className="h-full w-80 bg-white border-r border-gray-200 flex flex-col shadow-lg lg:shadow-none">
                    {/* Sidebar Header */}
                    <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-base font-semibold text-gray-900">Conversations</h2>
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
                                title="Close sidebar"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>
                        <button
                            onClick={createNewChat}
                            className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium text-sm"
                        >
                            <Plus className="w-4 h-4" />
                            <span>New Chat</span>
                        </button>
                    </div>

                    {/* Chat History List */}
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                        {chats.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                    <MessageSquare className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-sm text-gray-600 font-medium">No conversations yet</p>
                                <p className="text-xs text-gray-500 mt-1">Start a new chat to begin</p>
                            </div>
                        ) : (
                            chats.map((chat) => (
                                <div
                                    key={chat.id}
                                    className={`p-2.5 rounded-lg border transition-all ${
                                        currentChatId === chat.id
                                            ? 'border-primary-500 bg-primary-50 shadow-sm'
                                            : 'border-gray-200 hover:border-primary-300 hover:shadow-sm'
                                    }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div
                                            onClick={() => selectChat(chat.id)}
                                            className="cursor-pointer flex-1"
                                        >
                                            <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-1.5">
                                                {chat.title}
                                            </p>
                                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                                                <Clock className="w-3 h-3" />
                                                <span>{formatTime(chat.timestamp)}</span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end ml-2">
                                            {currentChatId === chat.id && (
                                                <span className="text-xs text-primary-600 font-medium mb-1.5">
                                                    Viewing
                                                </span>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteConversation(chat.id);
                                                }}
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete conversation"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Database Info */}
                </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
                {/* Top Bar */}
                <div className="px-6 py-3 border-b border-gray-200 bg-white flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
                            >
                                <Menu className="w-5 h-5 text-gray-600" />
                            </button>
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Back to agents"
                            >
                                <ArrowLeft className="w-5 h-5 text-gray-600" />
                            </button>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">{currentDatabase.name}</h1>
                                <div className="flex items-center space-x-2 mt-0.5">
                                    <Database className="w-3 h-3 text-primary-600" />
                                    <span className="text-xs text-gray-600">Connected to database</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto bg-gray-50 min-h-0">
                    {!currentChat ? (
                        <div className="flex flex-col items-center justify-center min-h-full px-6">
                            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                                <Bot className="w-8 h-8 text-primary-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2 text-center px-4">
                                You are connected to {currentDatabase.name}
                            </h2>
                            <p className="text-gray-600 text-center max-w-md mb-6 px-4 text-sm">
                                Ask questions about your database and get AI-powered insights with detailed analysis.
                            </p>
                            <div className="w-full max-w-2xl">
                                <div className="flex items-center space-x-3">
                                    <input
                                        type="text"
                                        value={question}
                                        onChange={(e) => setQuestion(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Ask a question about your database..."
                                        disabled={loading}
                                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                                    />
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={loading || !question.trim()}
                                        className={`px-5 py-3 rounded-lg font-medium flex items-center space-x-2 transition-all ${
                                            loading || !question.trim()
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-primary-600 text-white hover:bg-primary-700'
                                        }`}
                                    >
                                        <Send className="w-4 h-4" />
                                        <span>{loading ? 'Analyzing...' : 'Send'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-5xl mx-auto px-6 py-3 pb-3">
                            {currentChat.messages.map((message, index) => (
                                <div key={message.id} className="mb-4">
                                    {message.role === 'user' ? (
                                        <>
                                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                <User className="w-5 h-5 text-gray-600" />
                                            </div>
                                            <div className="flex-1 ml-4">
                                                <div className="bg-gray-50 rounded-lg px-4 py-3 border-l-4 border-gray-400">
                                                    <p className="text-xs font-medium text-gray-500 mb-1">Your Question</p>
                                                    <p className="text-sm text-gray-900">{message.content}</p>
                                                </div>
                                                <div className="flex items-center space-x-2 mt-1.5 text-xs text-gray-500">
                                                    <Clock className="w-3 h-3" />
                                                    <span>{formatTime(message.timestamp)}</span>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                                                <Bot className="w-5 h-5 text-white" />
                                            </div>
                                            <div className="flex-1 ml-4">
                                                <div className="bg-primary-50 rounded-lg px-4 py-3 border-l-4 border-primary-600">
                                                    <p className="text-xs font-medium text-primary-600 mb-2">AI Response</p>

                                                    {/* AI Response Content */}
                                                    {message.isLoading ? (
                                                        <div className="flex items-center space-x-2 text-sm text-gray-700">
                                                            <Loader className="w-4 h-4 animate-spin text-primary-600" />
                                                            <span>Analyzing your question...</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="text-sm text-gray-900 prose prose-blue max-w-none">
                                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                    {message.content}
                                                                </ReactMarkdown>
                                                            </div>

                                                            {/* Parameter Input Form (Phase 1) */}
                                                            {message.waitingForParameters && message.queriesWithParameters && message.queriesWithParameters.length > 0 && (
                                                                <div className="mt-4 bg-white rounded-lg border border-primary-200 overflow-hidden">
                                                                    <div className="px-4 py-3 bg-primary-50 border-b border-primary-200">
                                                                        <h4 className="font-semibold text-primary-900 text-sm">Query Parameters</h4>
                                                                        <p className="text-xs text-primary-700 mt-0.5">Please provide values for the following parameters</p>
                                                                    </div>
                                                                    <div className="p-4 space-y-4">
                                                                        {message.queriesWithParameters.map((queryInfo, qIdx) => (
                                                                            <div key={qIdx} className="border border-gray-200 rounded-lg p-4">
                                                                                <div className="flex items-start justify-between mb-3">
                                                                                    <div className="flex-1">
                                                                                        <h5 className="font-semibold text-gray-900 text-sm">{queryInfo.agent_name || `Query ${qIdx + 1}`}</h5>
                                                                                        {queryInfo.task && (
                                                                                            <p className="text-xs text-gray-600 mt-1">{queryInfo.task}</p>
                                                                                        )}
                                                                                        {queryInfo.selection_reason && (
                                                                                            <p className="text-xs text-primary-600 mt-1 flex items-center">
                                                                                                <span className="mr-1">💡</span>
                                                                                                {queryInfo.selection_reason}
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                                                                        queryInfo.execution_status === 'pending_parameters' 
                                                                                            ? 'bg-yellow-100 text-yellow-700' 
                                                                                            : 'bg-gray-100 text-gray-700'
                                                                                    }`}>
                                                                                        {queryInfo.execution_status || 'pending'}
                                                                                    </span>
                                                                                </div>

                                                                                {/* SQL Query Display */}
                                                                                {queryInfo.sql && (
                                                                                    <div className="mb-3">
                                                                                        <p className="text-xs font-medium text-gray-500 mb-1">SQL Query:</p>
                                                                                        <div className="bg-gray-50 rounded p-2 overflow-x-auto">
                                                                                            <code className="text-xs text-gray-700">{queryInfo.sql}</code>
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {/* Parameter Inputs */}
                                                                                {queryInfo.parameters && queryInfo.parameters.length > 0 ? (
                                                                                    <div className="space-y-3">
                                                                                        <p className="text-xs font-medium text-gray-700">Parameters:</p>
                                                                                        {queryInfo.parameters.map((param, pIdx) => (
                                                                                            <div key={pIdx}>
                                                                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                                                                    {param.name.replace(/_/g, ' ')}
                                                                                                    {param.description && (
                                                                                                        <span className="block text-xs text-gray-500 font-normal mt-0.5">
                                                                                                            {param.description}
                                                                                                        </span>
                                                                                                    )}
                                                                                                </label>
                                                                                                <input
                                                                                                    type={param.type === 'number' || param.type === 'integer' ? 'number' : 'text'}
                                                                                                    value={
                                                                                                        userParameters[queryInfo.generated_query_ids?.[0]]?.[param.name] || 
                                                                                                        param.default || 
                                                                                                        ''
                                                                                                    }
                                                                                                    onChange={(e) => handleParameterChange(
                                                                                                        queryInfo.generated_query_ids?.[0],
                                                                                                        param.name,
                                                                                                        e.target.value
                                                                                                    )}
                                                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                                                                                    placeholder={param.default || `Enter ${param.name}`}
                                                                                                />
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <p className="text-xs text-gray-500 italic">No parameters required for this query</p>
                                                                                )}
                                                                            </div>
                                                                        ))}

                                                                        {/* Execute Button */}
                                                                        <div className="flex justify-end pt-3 border-t border-gray-200">
                                                                            <button
                                                                                onClick={handleExecutePhase2}
                                                                                disabled={isPhase2Executing}
                                                                                className={`px-6 py-2.5 text-white rounded-lg font-semibold flex items-center justify-center gap-2 shadow-md transition-colors ${
                                                                                    isPhase2Executing
                                                                                        ? 'bg-primary-400 cursor-not-allowed'
                                                                                        : 'bg-primary-600 hover:bg-primary-700'
                                                                                }`}
                                                                            >
                                                                                {isPhase2Executing ? (
                                                                                    <>
                                                                                        <Loader className="w-4 h-4 animate-spin" />
                                                                                        <span>Executing...</span>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <Play className="w-4 h-4" />
                                                                                        <span>Execute Queries</span>
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Analysis Results */}
                                                            {(message.results && message.results.length > 0) || (message.stepExecutionSummary && message.stepExecutionSummary.length > 0) ? (
                                                                <div className="mt-3 space-y-2">
                                                                    {/* Step Execution Summary */}
                                                                    {message.stepExecutionSummary && message.stepExecutionSummary.length > 0 && (
                                                                        renderStepExecutionSummary(message.stepExecutionSummary)
                                                                    )}

                                                                    {/* Results */}
                                                                    {message.results && message.results.length > 0 && (
                                                                        <div>
                                                                            <div className="flex items-center justify-between mb-2">
                                                                                <p className="text-xs font-semibold text-primary-700">
                                                                                    Analysis Results 
                                                                                    {message.stepsCompleted > 0 && (
                                                                                        <span className="ml-2 text-primary-600 font-normal">
                                                                                            ({message.stepsCompleted}/{message.totalSteps} agents completed)
                                                                                        </span>
                                                                                    )}
                                                                                </p>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        const allExpanded = message.results.every((_, idx) => expandedResults[idx]);
                                                                                        const newExpandedState = {};
                                                                                        message.results.forEach((_, idx) => {
                                                                                            newExpandedState[idx] = !allExpanded;
                                                                                        });
                                                                                        setExpandedResults(newExpandedState);
                                                                                    }}
                                                                                    className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                                                                                >
                                                                                    {message.results.every((_, idx) => expandedResults[idx]) ? 'Collapse All' : 'Expand All'}
                                                                                </button>
                                                                            </div>
                                                                            {message.results.map((result, idx) => {
                                                                                // Render result if it has data
                                                                                if (result && (result.data || result.execution)) {
                                                                                    return renderResultCard(result, idx, currentChat.id, index);
                                                                                }
                                                                                return null;
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : null}

                                                            {/* Reasoning */}
                                                            {message.reasoning && (
                                                                <div className="mt-3 pt-2 border-t border-primary-200">
                                                                    <p className="text-xs font-medium text-primary-700 mb-1">Analysis Process</p>
                                                                    <p className="text-xs text-gray-700">{message.reasoning}</p>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-2 mt-1.5 text-xs text-gray-500">
                                                    <Bot className="w-3 h-3" />
                                                    <span>AI Agent</span>
                                                    <span>•</span>
                                                    <span className="flex items-center space-x-1">
                                                        <Clock className="w-3 h-3" />
                                                        <span>{formatTime(message.timestamp)}</span>
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Parameter Input Dialog */}
            {showParameterDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        {/* Dialog Header */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">Provide Query Parameters</h3>
                            <button
                                onClick={handleParameterCancel}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Dialog Content */}
                        <div className="px-6 py-4 space-y-4">
                            <p className="text-sm text-gray-600">
                                Please provide values for the following parameters to execute the query:
                            </p>

                            {parameterRequirements.map((param) => (
                                <div key={param.name} className="space-y-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        {param.name}
                                        {param.required && <span className="text-red-500 ml-1">*</span>}
                                    </label>
                                    <p className="text-xs text-gray-500 mb-1">{param.description}</p>
                                    
                                    {param.type === 'date' ? (
                                        <input
                                            type="date"
                                            value={parameterValues[param.name] || ''}
                                            onChange={(e) => handleLocalParameterChange(param.name, e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                            required={param.required}
                                        />
                                    ) : param.type === 'integer' || param.type === 'float' ? (
                                        <input
                                            type="number"
                                            value={parameterValues[param.name] || ''}
                                            onChange={(e) => handleLocalParameterChange(param.name, e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                            placeholder={`Enter ${param.type}`}
                                            required={param.required}
                                        />
                                    ) : param.type === 'boolean' ? (
                                        <select
                                            value={parameterValues[param.name] || ''}
                                            onChange={(e) => handleLocalParameterChange(param.name, e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                        >
                                            <option value="">Select...</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            value={parameterValues[param.name] || ''}
                                            onChange={(e) => handleLocalParameterChange(param.name, e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                            placeholder={param.default || `Enter ${param.name}`}
                                            required={param.required}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Dialog Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                            <button
                                onClick={handleParameterCancel}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleParameterSubmit}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center space-x-2"
                            >
                                <Send className="w-4 h-4" />
                                <span>Execute Query</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Agent Sufficiency Modal */}
            {showAgentSufficiencyModal && agentSufficiencyData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                                <AlertCircle className="w-5 h-5 text-amber-500" />
                                <span>Additional Agents Required</span>
                            </h3>
                            <button
                                onClick={handleCancelAgentCreation}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                disabled={isCreatingAgents}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="px-6 py-4 space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                <p className="text-sm text-amber-800">
                                    <strong>Analysis:</strong> {agentSufficiencyData.reasoning || 'The existing agents are not sufficient to answer this question comprehensively.'}
                                </p>
                            </div>

                            {agentSufficiencyData.existing_agents_count === 0 ? (
                                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                                    <p className="text-sm text-primary-800">
                                        <strong>No agents available.</strong> You don't have any sub-agents configured for this database yet.
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                                    <p className="text-sm text-primary-800">
                                        <strong>Existing agents:</strong> You have {agentSufficiencyData.existing_agents_count} sub-agent(s) configured, but additional expertise is needed.
                                    </p>
                                </div>
                            )}

                            {agentSufficiencyData.required_agents && agentSufficiencyData.required_agents.length > 0 && (
                                <div className="space-y-3">
                                    <h4 className="font-medium text-gray-900">Suggested New Agents:</h4>
                                    <div className="space-y-2">
                                        {agentSufficiencyData.required_agents.map((agent, idx) => (
                                            <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <h5 className="font-medium text-gray-900 text-sm">
                                                            {agent.name || `Agent ${idx + 1}`}
                                                        </h5>
                                                        {agent.focus && (
                                                            <p className="text-xs text-gray-600 mt-1">
                                                                <strong>Focus:</strong> {agent.focus}
                                                            </p>
                                                        )}
                                                        {agent.description && (
                                                            <p className="text-xs text-gray-600 mt-1">
                                                                <strong>Description:</strong> {agent.description}
                                                            </p>
                                                        )}
                                                        {agent.tables && agent.tables.length > 0 && (
                                                            <p className="text-xs text-gray-600 mt-1">
                                                                <strong>Tables:</strong> {agent.tables.join(', ')}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                                <p className="text-sm text-gray-700 mb-3">
                                    <strong>What would you like to do?</strong>
                                </p>
                                <ul className="space-y-2 text-sm text-gray-600">
                                    <li className="flex items-start space-x-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 flex-shrink-0"></div>
                                        <span><strong>Create suggested agents:</strong> We'll create the recommended agents and proceed to answer your question</span>
                                    </li>
                                    <li className="flex items-start space-x-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                                        <span><strong>Proceed without creating:</strong> We'll attempt to answer with existing agents (may be limited)</span>
                                    </li>
                                    <li className="flex items-start space-x-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                                        <span><strong>Cancel:</strong> Close this dialog without taking action</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                            <button
                                onClick={handleCancelAgentCreation}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                disabled={isCreatingAgents}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleProceedWithoutCreatingAgents}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                                disabled={isCreatingAgents}
                            >
                                Proceed Without Creating
                            </button>
                            <button
                                onClick={handleCreateAgents}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
                                disabled={isCreatingAgents}
                            >
                                {isCreatingAgents ? (
                                    <>
                                        <Loader className="w-4 h-4 animate-spin" />
                                        <span>Creating Agents...</span>
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        <span>Create & Answer Question</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal for Conversations */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmDeleteConversation}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />

            {/* Confirmation Modal for Execution Results */}
            <ConfirmModal
                isOpen={executionConfirmModal.isOpen}
                onClose={() => setExecutionConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmDeleteExecution}
                title={executionConfirmModal.title}
                message={executionConfirmModal.message}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />
        </div>
    );
};
